import { exportDocx } from "@tiptap-pro/extension-export-docx";
import type { JSONContent } from "@tiptap/core";
import type { Editor } from "@tiptap/react";
import { exportTiptapPdf } from "src/services/backend";
import { downloadFile } from "src/utils";
import {
  createResolvedTiptapExportDocument,
  createResolvedTiptapExportMarkdown,
  type DynamicNodeExportResolver,
} from "./tiptapDynamicExportNodes";
import { prepareTiptapExportImages } from "./tiptapExportImages";
import {
  addExportMetadata,
  buildExportMetadataMarkdown,
  type ExportMetadataContext,
} from "./exportMetadata";
import { TIPTAP_SHARED_EXPORT_OPTIONS } from "./tiptapExportStyles";

export type TiptapExportFormat = "docx" | "markdown" | "pdf";

type EditorWithMarkdownSerializer = Editor & {
  markdown?: { serialize: (document: JSONContent) => string };
};

type CommonExportParams = {
  document: JSONContent;
  exportMetadataContext: ExportMetadataContext;
  fileName: string;
  resolver: DynamicNodeExportResolver;
};

const DOCX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const MARKDOWN_CONTENT_TYPE = "text/markdown;charset=utf-8";
const PDF_CONTENT_TYPE = "application/pdf";
// eslint-disable-next-line no-control-regex -- These are the exact XML 1.0-illegal C0 characters.
const XML_ILLEGAL_C0_CONTROLS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

const sanitizeXmlStringValues = (value: unknown): unknown => {
  if (typeof value === "string") {
    return value.replace(XML_ILLEGAL_C0_CONTROLS, "");
  }

  if (Array.isArray(value)) {
    return value.map(sanitizeXmlStringValues);
  }

  if (typeof value === "object" && value !== null) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        sanitizeXmlStringValues(nestedValue),
      ])
    );
  }

  return value;
};

export const sanitizeTiptapExportDocument = (
  document: JSONContent
): JSONContent => sanitizeXmlStringValues(document) as JSONContent;

export const getTiptapExportFileName = (
  name: string | null | undefined,
  extension: "docx" | "md" | "pdf"
) => {
  const baseName = name?.trim() || "document";
  return baseName.toLowerCase().endsWith(`.${extension}`)
    ? baseName
    : `${baseName}.${extension}`;
};

const prepareResolvedDocument = ({
  document,
  exportMetadataContext,
  resolver,
}: {
  document: JSONContent;
  exportMetadataContext: ExportMetadataContext;
  resolver: DynamicNodeExportResolver;
}) =>
  addExportMetadata(
    createResolvedTiptapExportDocument(document, resolver),
    exportMetadataContext
  );

const typedBlob = (blob: Blob, contentType: string) =>
  blob.type ? blob : new Blob([blob], { type: contentType });

const prepareVisualExportDocument = async (
  params: CommonExportParams,
  target: "docx" | "pdf"
) =>
  sanitizeTiptapExportDocument(
    await prepareTiptapExportImages(prepareResolvedDocument(params), target)
  );

export const downloadTiptapDocx = async (params: CommonExportParams) => {
  const document = await prepareVisualExportDocument(params, "docx");
  const result = await exportDocx({
    ...TIPTAP_SHARED_EXPORT_OPTIONS,
    document,
    exportType: "blob",
    customNodes: [],
  });
  const blob =
    result instanceof Blob
      ? typedBlob(result, DOCX_CONTENT_TYPE)
      : new Blob([result as BlobPart], {
          type: DOCX_CONTENT_TYPE,
        });

  await downloadFile(blob, params.fileName);
};

export const downloadTiptapPdf = async (
  params: CommonExportParams & { orgId: string }
) => {
  const document = await prepareVisualExportDocument(params, "pdf");
  const response = await exportTiptapPdf({
    document,
    fileName: params.fileName,
    orgId: params.orgId,
  });
  const pdfBlob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: PDF_CONTENT_TYPE });

  await downloadFile(typedBlob(pdfBlob, PDF_CONTENT_TYPE), params.fileName);
};

export const downloadTiptapMarkdown = async (
  params: CommonExportParams & { editor: EditorWithMarkdownSerializer }
) => {
  const markdownManager = params.editor.markdown;
  if (!markdownManager) {
    throw new Error(
      "Markdown export requires the @tiptap/markdown extension to be configured"
    );
  }

  const bodyMarkdown = createResolvedTiptapExportMarkdown(
    params.document,
    params.resolver,
    (document) => markdownManager.serialize(document)
  ).trimEnd();
  const markdown = [
    buildExportMetadataMarkdown(params.exportMetadataContext),
    bodyMarkdown,
  ]
    .filter(Boolean)
    .join("\n\n");

  await downloadFile(
    new Blob([markdown], { type: MARKDOWN_CONTENT_TYPE }),
    params.fileName
  );
};
